import _ from 'lodash';
import {
    AgencyProfileOption,
    DispatchAreaObject,
    LocationEntityLink,
    MapAssignedUnitInfo,
    MapLedgerUnitView,
    MultiAgencyClassificationDto,
    SubdivisionOption,
    SubdivisionOptionsBootstrapView,
    RelatedDataEntity,
    SummaryCategoryEnum,
    SummaryCategoryEnumType,
    UnitStateGroupEnum,
    MapInfoAgencyEventMaterializedCaution,
    MapLedgerEventView,
    AmlLocation,
} from '@mark43/cad-api';
import { GeoCautionDto, NearbyGeoCautionDto } from '@mark43/rms-api';
import { getUnitIconType } from '../../../esri/helpers/units';
import { getMapIconTheme } from '../../../esri/helpers/map-icon-theme';
import {
    emsUniqueValueInfos,
    fireUniqueValueInfos,
    policeUniqueValueInfos,
} from '../../../esri/helpers/stations';
import {
    AgencyTypeGlobal,
    agencyTypeIdToDisplay,
    globalAttributes,
} from '../../../common/global-attributes';
import { AgencyTypeCapitalized, GenericObject } from '../../../common/types';
import { metersToFeetRoundedForDisplay } from '../../../common/helpers/utils';
import {
    AgencyClassification,
    AgencySubdivisionGroup,
    JSDateInput,
    MapAssignedUnitInfoExtended,
    MultiAgencySubdivisionLabelProps,
    MultiAgencySubdivisionsDisplay,
    SingleAgencySubdivisionLabelProps,
} from './types';
import { EsriMapStore } from '../../stores/esri-map-store';
import { MAX_AGENCIES_TO_SHOW } from '../../configuration';
import { createCell, TSimpleTableProps } from '../../../common/components/simple-table';
import { LinkText } from '../../../common/components/link-text';
import { TextWithEllipsisAndTooltip } from '../../../common/components/text-with-ellipsis-and-tooltip';

export function isActive(startDateUtc: JSDateInput, endDateUtc: JSDateInput): boolean {
    const now = new Date();
    const startDate = new Date(startDateUtc);
    return startDate < now && (!endDateUtc || new Date(endDateUtc) > now);
}

const geoCautionLevels: GenericObject<number> = {
    'Priority 1': 1,
    'Priority 2': 2,
    'Priority 3': 3,
    'Priority 4': 4,
};

const policeSubdivisionAttrIdFields = [
    'subdivision1AttrId',
    'subdivision2AttrId',
    'subdivision3AttrId',
    'subdivision4AttrId',
    'subdivision5AttrId',
];
const fireSubdivisionAttrIdFields = [
    'subdivisionFire1AttrId',
    'subdivisionFire2AttrId',
    'subdivisionFire3AttrId',
    'subdivisionFire4AttrId',
    'subdivisionFire5AttrId',
];
const emsSubdivisionAttrIdFields = [
    'subdivisionMedical1AttrId',
    'subdivisionMedical2AttrId',
    'subdivisionMedical3AttrId',
    'subdivisionMedical4AttrId',
    'subdivisionMedical5AttrId',
];

export function groupByCategory(cautionList: GeoCautionDto[]) {
    return _(cautionList)
        .map((caution) => ({ ...caution, cautionLevel: geoCautionLevels[caution.priority] }))
        .groupBy('locationCautionCategoryAttrDisplay')
        .map((cautions, categoryHeader) => {
            const [{ cautionLevel }] = cautions;
            return {
                cautions,
                categoryHeader,
                cautionLevel,
            };
        })
        .orderBy(['cautionLevel', 'categoryHeader'])
        .value();
}

export function getCautionsGroupedByCategory(rawCautions: GeoCautionDto[]) {
    const { wholePremiseCautions, subPremiseCautions } = rawCautions
        .filter((caution) => isActive(caution.startDateUtc, caution.endDateUtc || ''))
        .reduce(
            (obj, caution) => {
                if (caution.subPremise) {
                    obj.subPremiseCautions.push(caution);
                } else {
                    obj.wholePremiseCautions.push(caution);
                }
                return obj;
            },
            { subPremiseCautions: [], wholePremiseCautions: [] } as Record<string, GeoCautionDto[]>
        );

    const byCategory = groupByCategory(wholePremiseCautions);
    const subPremise = _(subPremiseCautions)
        .groupBy('subPremise')
        .mapValues((val) => groupByCategory(val))
        .value();

    return {
        byCategory,
        subPremise,
    };
}

export function getNearbyCautionsGroupedByFullAddress(cautionList: NearbyGeoCautionDto[]) {
    return _(cautionList)
        .filter((caution) => isActive(caution.startDateUtc, caution.endDateUtc || ''))
        .map((caution) => ({ ...caution, cautionLevel: geoCautionLevels[caution.priority] }))
        .groupBy('fullAddress')
        .map((cautions, cautionFullAddress) => {
            const [{ distanceFeet, latitude, longitude }] = cautions;
            const sortedCautions = _(cautions)
                .orderBy(['cautionLevel', 'locationCautionAttrDisplay'])
                .value();

            return {
                cautionFullAddress,
                distanceFeet,
                latitude,
                longitude,
                cautions: sortedCautions,
            };
        })
        .orderBy(['distanceFeet'])
        .value();
}

/** Pass `unitStateId` to get the department's configured unit status color, when there is one. */
export function getUnitIconByUniqueValueKey(
    unitUniqueValueKey: string,
    unitStateId?: number | null
): string {
    if (!unitUniqueValueKey) {
        return '';
    }
    const mapIconTheme = getMapIconTheme();
    return mapIconTheme.getUnitIconUrl(
        mapIconTheme.getUnitIconName(unitUniqueValueKey, unitStateId)
    );
}

/** The side panel / search icon for an event, in its configured priority color when there is one. */
export function getEventIcon(
    isAssigned: boolean,
    priorityAttributeId: number | null | undefined,
    agencyTypeGlobalAttrId: number | null | undefined
): string {
    const mapIconTheme = getMapIconTheme();
    return mapIconTheme.getEventIconUrl(
        mapIconTheme.getEventIconName(
            isAssigned ? 'Assigned, Inactive' : 'Unassigned, Inactive',
            priorityAttributeId,
            agencyTypeGlobalAttrId
        )
    );
}

export function unitToSummaryCategory(
    mapLedgerUnit?: MapLedgerUnitView | MapAssignedUnitInfoExtended
): SummaryCategoryEnumType {
    const group = mapLedgerUnit?.unitStateGroup;
    const isFire = mapLedgerUnit?.agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.FIRE;
    const isEms = mapLedgerUnit?.agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.EMS;
    const isPolice =
        mapLedgerUnit?.agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;
    const CLASS_PREFIX = isFire ? 'FIRE_' : isEms ? 'EMS_' : '';

    switch (true) {
        case group === UnitStateGroupEnum.AVAILABLE.name:
            return `${CLASS_PREFIX}${SummaryCategoryEnum.UNIT_AVAILABLE.name}`;
        case (isPolice && group === UnitStateGroupEnum.ADMIN.name) ||
            (isPolice && group === UnitStateGroupEnum.SERVICE_ASSIGNMENT.name):
            return SummaryCategoryEnum.UNIT_ADMIN.name;
        case isPolice && group === UnitStateGroupEnum.DIRECTED_PATROL.name:
            return SummaryCategoryEnum.UNIT_DIRECTED_PATROL.name;
        case isPolice && group === UnitStateGroupEnum.VIRTUAL_PATROL.name:
            return SummaryCategoryEnum.UNIT_VIRTUAL_PATROL.name;
        case isPolice && group === UnitStateGroupEnum.SPECIAL_ASSIGNMENT.name:
            return SummaryCategoryEnum.UNIT_SPECIAL_ASSIGNMENT.name;
        case !!mapLedgerUnit?.assignedEventId:
            return `${CLASS_PREFIX}${SummaryCategoryEnum.UNIT_ASSIGNED.name}`;
        case group === UnitStateGroupEnum.UNMANNED.name:
            return isFire
                ? SummaryCategoryEnum.FIRE_UNIT_UNMANNED.name
                : SummaryCategoryEnum.UNIT_UNMANNED.name;
        default:
            return `${CLASS_PREFIX}${SummaryCategoryEnum.UNIT_UNAVAILABLE.name}`;
    }
}

export function getArrayForSearchItems<T>(
    agencyType: string,
    policeSubdivisionOptionsOrAttrIds?: T[] | string,
    fireSubdivisionOptionsOrAttrIds?: T[] | string,
    emsSubdivisionOptionsOrAttrIds?: T[] | string
): string | T[] | undefined {
    switch (agencyType) {
        case AgencyTypeCapitalized.POLICE:
            return policeSubdivisionOptionsOrAttrIds;
        case AgencyTypeCapitalized.FIRE:
            return fireSubdivisionOptionsOrAttrIds;
        default:
            return emsSubdivisionOptionsOrAttrIds;
    }
}

export function getSingleAgencySubdivisionLabel({
    agencyType,
    policeSubdivisionOptions,
    fireSubdivisionOptions,
    emsSubdivisionOptions,
    subdivisionAttrIds,
    subdivisionAttrId,
}: SingleAgencySubdivisionLabelProps): string | undefined {
    const optionsArrayToFind = getArrayForSearchItems(
        agencyType,
        policeSubdivisionOptions,
        fireSubdivisionOptions,
        emsSubdivisionOptions
    );
    const subdivisionLabel = (optionsArrayToFind as SubdivisionOption[])?.find(
        (option) => option.value === subdivisionAttrIds[subdivisionAttrId]
    )?.label;

    return subdivisionLabel;
}

export function fillSingleAgencySubdivisionItemsWithSubdivisionLabels(
    agencyType: string,
    subdivisionAttrIds: GenericObject<number>,
    arrayForSearch?: string[] | string,
    combinedSubdivisionOptionsBootstrapView?: SubdivisionOptionsBootstrapView
): string[] {
    const subdivisionItems: string[] = [];

    (arrayForSearch as string[])?.forEach((item, i) => {
        const policeSubdivisionOptions = `policeSubdivision${
            i + 1
        }Options` as keyof SubdivisionOptionsBootstrapView;

        const fireSubdivisionOptions = `fireSubdivision${
            i + 1
        }Options` as keyof SubdivisionOptionsBootstrapView;

        const emsSubdivisionOptions = `emsSubdivision${
            i + 1
        }Options` as keyof SubdivisionOptionsBootstrapView;

        if (item in subdivisionAttrIds) {
            const subdivisionLabel = getSingleAgencySubdivisionLabel({
                agencyType,
                policeSubdivisionOptions:
                    combinedSubdivisionOptionsBootstrapView?.[policeSubdivisionOptions],
                fireSubdivisionOptions:
                    combinedSubdivisionOptionsBootstrapView?.[fireSubdivisionOptions],
                emsSubdivisionOptions:
                    combinedSubdivisionOptionsBootstrapView?.[emsSubdivisionOptions],
                subdivisionAttrIds,
                subdivisionAttrId: item,
            });
            if (subdivisionLabel) {
                subdivisionItems.push(subdivisionLabel);
            }
        }
    });

    return subdivisionItems;
}

export function getSubdivisionItemsBySingleAgencyType(
    agencyType: string,
    entityLinks?: LocationEntityLink,
    combinedSubdivisionOptionsBootstrapView?: SubdivisionOptionsBootstrapView
): string {
    const subdivisionAttrIds: GenericObject<number> = {};
    const arrayForSearch = getArrayForSearchItems(
        agencyType,
        policeSubdivisionAttrIdFields,
        fireSubdivisionAttrIdFields,
        emsSubdivisionAttrIdFields
    );

    (arrayForSearch as string[])?.forEach((subdivisionAttrId) => {
        if (entityLinks && subdivisionAttrId in entityLinks) {
            subdivisionAttrIds[
                subdivisionAttrId
            ] = ((entityLinks as unknown) as GenericObject<number>)[subdivisionAttrId];
        }
    });

    const subdivisionItems = fillSingleAgencySubdivisionItemsWithSubdivisionLabels(
        agencyType,
        subdivisionAttrIds,
        arrayForSearch,
        combinedSubdivisionOptionsBootstrapView
    );

    return subdivisionItems?.reverse().join(' / ');
}

export function groupSubdivisionIdsByAgency(
    MultiAgencyClassificationDto: MultiAgencyClassificationDto[]
) {
    return MultiAgencyClassificationDto.map((agency) => {
        const {
            agencyId,
            agencyTypeGlobalAttrId,
            dispatchAreaId,
            dispatchAreaView,
            subdivision1AttrId,
            subdivision2AttrId,
            subdivision3AttrId,
            subdivision4AttrId,
            subdivision5AttrId,
        } = agency;

        const subdivisionIds = [
            subdivision1AttrId,
            subdivision2AttrId,
            subdivision3AttrId,
            subdivision4AttrId,
            subdivision5AttrId,
        ].filter((id): id is number => typeof id === 'number' && id > 0);

        return {
            agencyId,
            agencyTypeGlobalAttrId,
            dispatchAreaId,
            dispatchAreaView,
            subdivisionIds,
        };
    });
}

export function getDispatchAreaById(
    dispatchAreaId: number,
    allDispatchAreaObjects: DispatchAreaObject[]
): DispatchAreaObject | undefined {
    return allDispatchAreaObjects.find((obj) => obj.id === dispatchAreaId);
}

export function getDepartmentAgencyById(
    departmentAgencies: AgencyProfileOption[],
    agencyId: number
): AgencyProfileOption | undefined {
    return departmentAgencies.find((agency) => agency.value === agencyId);
}

export function checkIfAgencyIsDefaultByAgencyId(
    departmentAgencies: AgencyProfileOption[],
    agencyId: number
): boolean | undefined {
    return departmentAgencies.find((agency) => agency.value === agencyId)?.isDefault;
}

export function getMultiAgencySubdivision({
    agencyTypeGlobalAttrId,
    policeSubdivisionOptions,
    fireSubdivisionOptions,
    emsSubdivisionOptions,
    subdivisionId,
}: MultiAgencySubdivisionLabelProps): SubdivisionOption | undefined {
    const agencyType =
        agencyTypeIdToDisplay[(agencyTypeGlobalAttrId as unknown) as AgencyTypeGlobal].capitalize;

    const optionsArrayToFind = getArrayForSearchItems(
        agencyType,
        policeSubdivisionOptions,
        fireSubdivisionOptions,
        emsSubdivisionOptions
    );

    const subdivision = (optionsArrayToFind as SubdivisionOption[])?.find(
        (option) => option.value === subdivisionId
    );

    return subdivision;
}

export function getMultiAgencySubdivisionNumber(
    subdivisionId: number,
    agencyDto?: MultiAgencyClassificationDto
): string | undefined {
    if (!agencyDto) {
        return;
    }

    const subdivisionAttrIdEntry = Object.entries(agencyDto).find(([_, value]) => {
        return value === subdivisionId;
    });

    if (!subdivisionAttrIdEntry) {
        return;
    }

    const subdivisionAttrIdStringArrayIndex = 0;
    // subdivisionNumber position in 'subdivision1AttrId' string:
    // numbers 1-5 (all possible variants) will be on the 11th position in the str
    const subdivisionNumberCharPositionInString = 11;

    return subdivisionAttrIdEntry[subdivisionAttrIdStringArrayIndex][
        subdivisionNumberCharPositionInString
    ];
}

export function getSubdivisionByMultiAgencyType(
    agencyTypeGlobalAttrId: number,
    subdivisionId: number,
    agencyId: number,
    multiAgencyClassificationDtos?: MultiAgencyClassificationDto[],
    agencyIdToSubdivisionOptionsBootstrapView?: GenericObject<SubdivisionOptionsBootstrapView>
): SubdivisionOption | undefined {
    const agencyDto = multiAgencyClassificationDtos?.find((agency) => agency.agencyId === agencyId);
    const subdivisionOptions =
        agencyIdToSubdivisionOptionsBootstrapView?.[agencyId] ||
        ({} as SubdivisionOptionsBootstrapView);
    const subdivisionNumber = getMultiAgencySubdivisionNumber(subdivisionId, agencyDto);

    const policeSubdivisionOptions = `policeSubdivision${subdivisionNumber}Options` as keyof SubdivisionOptionsBootstrapView;
    const fireSubdivisionOptions = `fireSubdivision${subdivisionNumber}Options` as keyof SubdivisionOptionsBootstrapView;
    const emsSubdivisionOptions = `emsSubdivision${subdivisionNumber}Options` as keyof SubdivisionOptionsBootstrapView;

    return getMultiAgencySubdivision({
        agencyTypeGlobalAttrId,
        policeSubdivisionOptions: subdivisionOptions[policeSubdivisionOptions],
        fireSubdivisionOptions: subdivisionOptions[fireSubdivisionOptions],
        emsSubdivisionOptions: subdivisionOptions[emsSubdivisionOptions],
        subdivisionId,
    });
}

export function mapMultiAgencyClassificationDtoToDisplay(
    multiAgencyClassificationDtos: MultiAgencyClassificationDto[] = [],
    agencyProfileOptions: AgencyProfileOption[],
    allDispatchAreaObjects: DispatchAreaObject[],
    agencyIdToSubdivisionOptionsBootstrapView?: GenericObject<SubdivisionOptionsBootstrapView>
): MultiAgencySubdivisionsDisplay {
    if (!multiAgencyClassificationDtos || multiAgencyClassificationDtos.length === 0) {
        return {
            multiAgencySubdivisions: [],
            dispatchAreaByClass: {},
            isEverythingOOJ: true,
        };
    }

    let OOJDAreaCount = 0;
    const dispatchAreaByClass: Record<number, string> = {};
    const agenciesSubdivisions = groupSubdivisionIdsByAgency(multiAgencyClassificationDtos);
    const multiAgencySubdivisions = agenciesSubdivisions
        .filter(({ dispatchAreaView, dispatchAreaId, agencyTypeGlobalAttrId }) => {
            if (!dispatchAreaByClass[agencyTypeGlobalAttrId]) {
                const dispatchArea = dispatchAreaId
                    ? getDispatchAreaById(dispatchAreaId, allDispatchAreaObjects)
                    : undefined;
                if (dispatchArea?.isOutOfJurisdiction) {
                    OOJDAreaCount = OOJDAreaCount + 1;
                }

                dispatchAreaByClass[agencyTypeGlobalAttrId] = dispatchAreaView?.name || '--';
            }
            // filter out agencies' with OOJ values
            return !dispatchAreaView?.isOutOfJurisdiction;
        })
        .sort((a, b) => {
            // sort by default agencies first, then agency names a-z;
            const isADefaultAgency = checkIfAgencyIsDefaultByAgencyId(
                agencyProfileOptions,
                a.agencyId
            );
            const isBDefaultAgency = checkIfAgencyIsDefaultByAgencyId(
                agencyProfileOptions,
                b.agencyId
            );

            if (
                (isADefaultAgency && isBDefaultAgency) ||
                (!isADefaultAgency && !isBDefaultAgency)
            ) {
                const agencyAName =
                    getDepartmentAgencyById(agencyProfileOptions, a.agencyId)?.label || '';
                const agencyBName =
                    getDepartmentAgencyById(agencyProfileOptions, b.agencyId)?.label || '';
                return agencyAName > agencyBName ? 1 : agencyAName < agencyBName ? -1 : 0;
            } else if (isADefaultAgency) {
                return -1;
            }

            // isBDefaultAgency === true
            return 1;
        })
        .sort((a, b) => a.agencyTypeGlobalAttrId - b.agencyTypeGlobalAttrId) // sort POLICE => FIRE => EMS
        .map(({ agencyId, agencyTypeGlobalAttrId, dispatchAreaView, subdivisionIds }) => {
            const agencyName =
                getDepartmentAgencyById(agencyProfileOptions, agencyId)?.label || agencyId;

            let subdivisionsLabel = '';
            const subdivisionItems: SubdivisionOption[] = [];

            subdivisionIds.forEach((id: number) => {
                const subdivision = getSubdivisionByMultiAgencyType(
                    agencyTypeGlobalAttrId,
                    id,
                    agencyId,
                    multiAgencyClassificationDtos,
                    agencyIdToSubdivisionOptionsBootstrapView
                );
                if (subdivision) {
                    subdivisionItems.push(subdivision);
                }

                const newSubdivisionLabel = subdivision?.label;

                if (subdivisionsLabel === '' && newSubdivisionLabel) {
                    subdivisionsLabel = newSubdivisionLabel;
                } else {
                    subdivisionsLabel = `${newSubdivisionLabel} / ${subdivisionsLabel}`;
                }
            });

            return {
                agencyId,
                agencyName,
                agencyTypeGlobalAttrId,
                dispatchArea: dispatchAreaView?.name || '--',
                subdivisions: subdivisionItems,
                subdivisionsLabel,
            };
        });

    return {
        multiAgencySubdivisions,
        dispatchAreaByClass,
        isEverythingOOJ:
            multiAgencySubdivisions.length === 0 &&
            Object.keys(dispatchAreaByClass).length === OOJDAreaCount,
    };
}

export function groupAndSetShouldShowForEachAgency(showAll: boolean, agenciesTypesCount: number) {
    let nonFirstAgenciesShown = 0;

    // the returned function is designed to be used for the 1st arg of array.reduce()
    // where array type is AgencyClassification [], and the 2nd arg is Record<typeof agencyTypeGlobalAttrId, AgencySubdivisionGroup>
    return (groups: Record<string, AgencySubdivisionGroup>, agency: AgencyClassification) => {
        const group = groups[agency.agencyTypeGlobalAttrId];
        const hasRoomToShow = nonFirstAgenciesShown < MAX_AGENCIES_TO_SHOW - agenciesTypesCount;

        if (group.agencies.length === 0) {
            group.agencies.push({
                ...agency,
                shouldShow: true, // prioritize display first agency per class type
            });
        } else {
            group.agencies.push({
                ...agency,
                shouldShow: showAll || hasRoomToShow,
            });
            nonFirstAgenciesShown++;
        }

        return groups;
    };
}

export function generateAgencySubdivisionGroupedByClassType(
    multiAgencySubdivisions: AgencyClassification[],
    dispatchAreaByClass: Record<number, string>,
    agenciesToShow: number,
    showAll: boolean
) {
    const dispatchAreaByClassList = Object.entries(dispatchAreaByClass);

    const agenciesByClassType = dispatchAreaByClassList.reduce(
        (groups: Record<string, AgencySubdivisionGroup>, dispatchArea: [string, string]) => {
            const [agencyTypeGlobalAttrId, value] = dispatchArea;

            groups[agencyTypeGlobalAttrId] = {
                agencyTypeDisplay:
                    agencyTypeIdToDisplay[(agencyTypeGlobalAttrId as unknown) as AgencyTypeGlobal]
                        .uppercase,
                dispatchArea: value || '--',
                agencies: [],
            };

            return groups;
        },
        {}
    );

    return agenciesToShow > 0
        ? multiAgencySubdivisions.reduce(
              groupAndSetShouldShowForEachAgency(showAll, dispatchAreaByClassList.length),
              agenciesByClassType
          )
        : agenciesByClassType;
}

export function getUnitUniqueValueKey({
    selectedMapLedgerUnit,
    isLocationPotentiallyInaccurate,
    isUser,
}: {
    selectedMapLedgerUnit?: MapLedgerUnitView | MapAssignedUnitInfoExtended;
    isLocationPotentiallyInaccurate?: boolean;
    isUser?: boolean;
}): string {
    if (selectedMapLedgerUnit) {
        const { unitStateGroup, agencyTypeGlobalAttrId } = selectedMapLedgerUnit || {};

        const unitSummaryCategoryBasedOnUnitStateGroup = unitToSummaryCategory(
            selectedMapLedgerUnit
        );

        const agencyTypeGlobalValue =
            agencyTypeIdToDisplay[agencyTypeGlobalAttrId as AgencyTypeGlobal].capitalize;
        const unitIconType = getUnitIconType(
            unitStateGroup,
            isUser ? 'USER' : 'UNIT',
            unitSummaryCategoryBasedOnUnitStateGroup
        );
        const locationInaccurateState = isLocationPotentiallyInaccurate ? 'Offline' : 'Online';
        const isEnRouteAlternate = unitStateGroup === UnitStateGroupEnum.EN_ROUTE_ALTERNATE.name;

        const basicUniqueValueKey = `${agencyTypeGlobalValue}, ${unitIconType}, ${locationInaccurateState}, Inactive`;
        const unitUniqueValueKey = isEnRouteAlternate
            ? `${basicUniqueValueKey}, EN_ROUTE_ALTERNATE`
            : basicUniqueValueKey;

        return unitUniqueValueKey;
    }
    return '';
}

export function assignedUnitToMapLedgerUnit(
    mapStore: EsriMapStore,
    unit: MapAssignedUnitInfo
): MapAssignedUnitInfoExtended | undefined {
    const mapLedgerUnit = mapStore.getMapLedgerUnit(unit.entityId);
    const selectedMapLedgerEvent = mapStore.selectedMapLedgerEvent;

    if (mapLedgerUnit && selectedMapLedgerEvent) {
        return {
            ...unit,
            agencyTypeGlobalAttrId: mapLedgerUnit.agencyTypeGlobalAttrId,
            type: mapLedgerUnit.type,
            assignedEventId: selectedMapLedgerEvent.entityId,
        };
    }
}

export function getLocationConfidence({
    uncertaintyRadius,
    confidencePercentage,
}: Pick<AmlLocation, 'uncertaintyRadius' | 'confidencePercentage'>): string {
    if (_.isNil(uncertaintyRadius) && _.isNil(confidencePercentage)) {
        return '';
    }

    const radius = uncertaintyRadius
        ? `${metersToFeetRoundedForDisplay(uncertaintyRadius)} ft`
        : '';
    const percentage = confidencePercentage
        ? `${radius ? ', ' : ''}${Math.trunc(confidencePercentage)}%`
        : '';

    return `${radius}${percentage}`;
}

export function getStationIconByAgencyTypeGlobalAttrId(agencyTypeGlobalAttrId?: number): string {
    let station;

    const agencyTypeGlobalValue = agencyTypeGlobalAttrId
        ? agencyTypeIdToDisplay[agencyTypeGlobalAttrId as AgencyTypeGlobal].capitalize
        : '';
    const stationUniqueValueKey = `${agencyTypeGlobalValue}, Inactive`;

    if (agencyTypeGlobalValue === AgencyTypeCapitalized.POLICE) {
        station = policeUniqueValueInfos.find((station) => station.value === stationUniqueValueKey);
    } else if (agencyTypeGlobalValue === AgencyTypeCapitalized.FIRE) {
        station = fireUniqueValueInfos.find((station) => station.value === stationUniqueValueKey);
    } else if (agencyTypeGlobalValue === AgencyTypeCapitalized.EMS) {
        station = emsUniqueValueInfos.find((station) => station.value === stationUniqueValueKey);
    }

    return station ? station.symbol.url : '';
}

export function getStationStreetAddressDisplayValue(
    streetAddress?: string,
    locality?: string,
    adminArea1?: string,
    postalCode?: string
): string {
    if (!streetAddress) return '';

    if (locality || adminArea1 || postalCode) {
        return `${streetAddress}, `;
    } else {
        return streetAddress;
    }
}

export function getStationLocalityDisplayValue(
    locality?: string,
    adminArea1?: string,
    postalCode?: string
): string {
    if (!locality) return '';

    if (adminArea1 || postalCode) {
        return `${locality}, `;
    } else {
        return locality;
    }
}

export function getStationAdminArea1DisplayValue(adminArea1?: string, postalCode?: string): string {
    if (!adminArea1) return '';

    if (postalCode) {
        return `${adminArea1}, `;
    } else {
        return adminArea1;
    }
}

export function generatePriorCadEventsRowData(
    relatedEntities: RelatedDataEntity[],
    onClick: (entityId: number) => void
): TSimpleTableProps['rows'] {
    return relatedEntities.map((relatedEntity) => {
        const entityNumberDigits = relatedEntity.entityNumber?.slice(-4);
        return [
            createCell({
                content: (
                    <LinkText
                        key="case"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick(relatedEntity.entityId);
                        }}
                    >
                        {entityNumberDigits}
                    </LinkText>
                ),
            }),
            createCell({ content: <TextWithEllipsisAndTooltip text={relatedEntity.title} /> }),
            createCell({
                content: <TextWithEllipsisAndTooltip text={relatedEntity.disposition} />,
            }),
        ];
    });
}

export function getGeoCautionsFromAgencyEventMaterializedCautions(
    agencyEventMaterializedCautions?: MapInfoAgencyEventMaterializedCaution[]
): GeoCautionDto[] | undefined {
    if (!agencyEventMaterializedCautions) return;

    return agencyEventMaterializedCautions.map(
        (agencyEventCaution): GeoCautionDto => {
            return {
                ...agencyEventCaution,
                isNearbyCaution: agencyEventCaution.isNearby,
                description: agencyEventCaution.cautionDescription,
                startDateUtc: agencyEventCaution.geoCautionStartDateUtc ?? '',
                fullAddress: agencyEventCaution.fullAddress ?? '',
                priority: agencyEventCaution.priority ?? '',
                updatedDateUtc: agencyEventCaution.geoCautionUpdatedDateUtc,
            };
        }
    );
}

export function getNearbyGeoCautionsFromAgencyEventMaterializedCautions(
    agencyEventMaterializedCautions?: MapInfoAgencyEventMaterializedCaution[]
): NearbyGeoCautionDto[] | undefined {
    if (!agencyEventMaterializedCautions) return;

    return agencyEventMaterializedCautions.map(
        (agencyEventCaution): NearbyGeoCautionDto => {
            return {
                ...agencyEventCaution,
                isNearbyCaution: agencyEventCaution.isNearby,
                description: agencyEventCaution.cautionDescription,
                startDateUtc: agencyEventCaution.geoCautionStartDateUtc ?? '',
                fullAddress: agencyEventCaution.fullAddress ?? '',
                priority: agencyEventCaution.priority ?? '',
                updatedDateUtc: agencyEventCaution.geoCautionUpdatedDateUtc,
                distanceFeet: agencyEventCaution.distanceToCaution ?? 0,
            };
        }
    );
}

export function getEventAssignedUnits(event: MapLedgerEventView) {
    return _.get(event, 'assignedUnits', []);
}

export function getIsUnitAssignedToEvent(event: MapLedgerEventView, unitId: number) {
    const units = getEventAssignedUnits(event);
    return _.some(units, { entityId: unitId });
}
